// @flow
import { BEDROOM_OPTIONS } from '../../ProspectProfile/ProspectProfileForm/LookingFor/constants';
import { NO_PREFERENCE_KEY } from '../../../utils/noPreference';
import type { FloorPlan } from '../../ManageFloorPlans/types';

type Option = { value: string, text: string };
type FloorPlanOption = { value: string, text: string, nBeds: number | string };

// Bedroom counts the property actually offers, following the derivation in
// LookingFor.hook.js: floor plans carry a numeric nBeds, BEDROOM_OPTIONS values
// are strings, so both sides are bridged through Number().
export const getBedroomOptions = (
  floorPlans: ?Array<FloorPlan>,
): Array<Option> => {
  const availableNBeds = new Set(
    (floorPlans ?? []).map(({ nBeds }) => Number(nBeds)),
  );

  return BEDROOM_OPTIONS.filter(({ value }) =>
    availableNBeds.has(Number(value)),
  );
};

// A stored size the property has no floor plan for is dropped without a trace,
// which also absorbs the dirty production shapes ([2], ["NaN"]). Seeding it
// instead would manufacture a waitlist position nobody can be housed in.
export const getPrefilledBedrooms = ({
  nBedsArr,
  nBeds,
  bedroomOptions,
}: {
  nBedsArr?: ?Array<string | number>,
  nBeds?: ?number,
  bedroomOptions?: ?Array<Option>,
}): Array<string> => {
  // nBeds is the column nBedsArr replaced, and is still the only answer on the
  // rows written before the array existed.
  const bridgedScalar = nBeds == null ? [] : [nBeds];
  const stored = nBedsArr?.length ? nBedsArr : bridgedScalar;
  const offerable = new Set((bedroomOptions ?? []).map(({ value }) => value));

  return stored
    .map(String)
    .filter((value) => value === NO_PREFERENCE_KEY || offerable.has(value));
};

export const filterFloorPlanOptions = (
  floorPlanOptions: ?Array<FloorPlanOption>,
  selectedBedrooms: ?Array<string>,
): Array<FloorPlanOption> => {
  const options = floorPlanOptions ?? [];
  const bedrooms = selectedBedrooms ?? [];
  const narrowsByBedroom =
    bedrooms.length > 0 && !bedrooms.includes(NO_PREFERENCE_KEY);

  if (!narrowsByBedroom) {
    return options;
  }

  return options.filter(
    ({ nBeds }) =>
      nBeds === NO_PREFERENCE_KEY ||
      bedrooms.some((bedroom) => Number(bedroom) === nBeds),
  );
};
